/** @module Single entry point to the backend API; wraps the generated OpenAPI client. */
import createClient, { type Middleware } from "openapi-fetch";

import type { components, paths } from "@/api/generated/schema";

export type { components, paths };

// Relative in the browser; tests and tooling can point at an absolute origin.
// Resolve fetch per call instead of capturing it, so tests can replace it.
const lateBoundFetch: typeof globalThis.fetch = (input, init) => globalThis.fetch(input, init);

export const api = createClient<paths>({
  baseUrl: import.meta.env.VITE_API_BASE_URL ?? "/",
  fetch: lateBoundFetch,
});

// The middleware stashes problem bodies here; they cannot be read twice.
const problems = new WeakMap<Response, Problem>();

type TokenProvider = () => string | null;
type Reauthenticate = () => Promise<string | null>;

let readToken: TokenProvider = () => null;
let reauthenticate: Reauthenticate | null = null;

/** The session module registers itself here; the client must not import it back. */
export function setAuthProvider(provider: TokenProvider, onUnauthorized?: Reauthenticate): void {
  readToken = provider;
  reauthenticate = onUnauthorized ?? null;
}

// Paths that must not trigger a refresh: they are the refresh flow itself.
const PUBLIC_PATHS = ["/api/v1/auth/login", "/api/v1/auth/refresh", "/api/v1/health"];

function isPublic(url: string): boolean {
  return PUBLIC_PATHS.some((path) => url.includes(path));
}

const authMiddleware: Middleware = {
  onRequest({ request }) {
    const token = readToken();
    if (token && !isPublic(request.url)) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    return request;
  },
  async onResponse({ request, response }) {
    // Read the problem body here: once openapi-fetch has parsed it, the body is gone.
    if (!response.ok) {
      try {
        const body: unknown = await response.clone().json();
        if (isProblem(body)) problems.set(response, body);
      } catch {
        /* not a problem document */
      }
    }
    if (response.status !== 401 || isPublic(request.url) || !reauthenticate) {
      return response;
    }
    // An expired access token is normal; try once with a fresh one before giving up.
    const token = await reauthenticate();
    if (!token) {
      return response;
    }
    const retry = request.clone();
    retry.headers.set("Authorization", `Bearer ${token}`);
    return globalThis.fetch(retry);
  },
};

api.use(authMiddleware);

/** Problem details as returned by the backend (RFC 9457). */
export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  code: string;
}

export function isProblem(value: unknown): value is Problem {
  return (
    typeof value === "object" &&
    value !== null &&
    "code" in value &&
    typeof (value as Problem).code === "string"
  );
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  static from(status: number, body: unknown): ApiError {
    if (isProblem(body)) {
      return new ApiError(status, body.code, body.detail ?? body.title);
    }
    return new ApiError(status, "unexpected_response", `Unexpected response (${status})`);
  }
}

/** Turn a failed response into an ApiError, using the problem the middleware kept. */
export function problemFrom(response: Response): ApiError {
  return ApiError.from(response.status, problems.get(response) ?? null);
}
